// src/cockpit4/components/ApprovalFlowEngine.tsx
// ============================================
// 审批流引擎 - 工作流中心 P1-功能7
//
// 功能：
//   1. 多级审批流程（支持任意层级），审批节点可配置（审批人/审批方式/超时处理）
//   2. 三种审批模式：会签(countersign) / 或签(any_sign) / 依次审批(sequential)
//   3. 审批操作（通过/拒绝/转交），含审批意见和附件
//   4. 审批流程可视化（节点链 + 当前进度 + 节点状态 + 审批意见记录）
//   5. 超时处理（定时检查超时节点，按配置自动通过/自动拒绝/转交）
//   6. 审批统计和分析（待审批/已审批/我发起的/平均耗时/通过率，按类型/按人统计）
//   7. 审批模板（费用报销/合同/内容发布，其余类型可自定义节点）
//
// 数据来源（白皮书铁律一：禁止假数据）：
//   - 审批记录：localStorage(key: xeaos_workflow_approvals) 持久化
//   - 当前用户：useUserStore（../../stores/userStore）
//   无数据时显示"暂无审批 / 点击发起"，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Gavel,
  Inbox,
  ListChecks,
  Paperclip,
  Plus,
  Send,
  Settings2,
  Timer,
  Trash2,
  TrendingUp,
  User,
  UserCheck,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { useUserStore } from "../../stores/userStore";

// ============================================
// 类型定义（与数据模型对齐）
// ============================================

/** 审批模式：会签 / 或签 / 依次审批 */
type ApprovalMode = "countersign" | "any_sign" | "sequential";

/** 审批结果 */
type ApprovalResult = "approved" | "rejected" | "pending" | "timeout" | "transferred";

/** 审批类型 */
type ApprovalType =
  | "expense"
  | "contract"
  | "leave"
  | "procurement"
  | "content_publish"
  | "custom";

/** 超时处理动作 */
type TimeoutAction = "auto_approve" | "auto_reject" | "transfer";

/** 审批记录 */
interface ApprovalRecord {
  id: string;
  approver: string;
  result: ApprovalResult;
  comment: string;
  attachments?: string[];
  timestamp: string;
}

/** 审批节点 */
interface ApprovalNode {
  id: string;
  name: string;
  approvers: string[];
  mode: ApprovalMode;
  order: number;
  timeoutHours: number;
  timeoutAction: TimeoutAction;
  transferTo?: string;
  status: ApprovalResult;
  records: ApprovalRecord[];
  /** 节点激活时间（成为当前节点时设置，用于超时判定） */
  activatedAt?: string;
}

/** 审批流 */
interface ApprovalFlow {
  id: string;
  title: string;
  type: ApprovalType;
  content: string;
  amount?: number;
  applicant: string;
  nodes: ApprovalNode[];
  currentNodeIndex: number;
  status: ApprovalResult;
  createdAt: string;
  completedAt?: string;
  totalDuration?: number; // 总耗时（毫秒）
}

/** 审批模板节点配置（用于初始化） */
interface TemplateNodeConfig {
  name: string;
  mode: ApprovalMode;
  timeoutHours: number;
  timeoutAction: TimeoutAction;
}

// ============================================
// 常量
// ============================================

const STORAGE_KEY = "xeaos_workflow_approvals";
/** 超时检查间隔（毫秒） */
const TIMEOUT_CHECK_INTERVAL = 60_000;

/** 审批模板（费用报销 / 合同 / 内容发布） */
const APPROVAL_TEMPLATES: Record<
  "expense" | "contract" | "content_publish",
  { title: string; nodes: TemplateNodeConfig[] }
> = {
  expense: {
    title: "费用报销审批",
    nodes: [
      { name: "部门经理", mode: "any_sign", timeoutHours: 24, timeoutAction: "transfer" },
      { name: "财务主管", mode: "any_sign", timeoutHours: 24, timeoutAction: "auto_reject" },
      { name: "总经理", mode: "any_sign", timeoutHours: 48, timeoutAction: "auto_reject" },
    ],
  },
  contract: {
    title: "合同审批",
    nodes: [
      { name: "部门经理", mode: "countersign", timeoutHours: 24, timeoutAction: "transfer" },
      { name: "法务审核", mode: "any_sign", timeoutHours: 48, timeoutAction: "auto_reject" },
      { name: "总经理", mode: "any_sign", timeoutHours: 48, timeoutAction: "auto_reject" },
    ],
  },
  content_publish: {
    title: "内容发布审批",
    nodes: [
      { name: "内容主管", mode: "any_sign", timeoutHours: 4, timeoutAction: "auto_approve" },
      { name: "品牌总监", mode: "any_sign", timeoutHours: 8, timeoutAction: "auto_approve" },
    ],
  },
};

/** 审批类型展示配置 */
const TYPE_CONFIG: Record<ApprovalType, { label: string; color: string }> = {
  expense: { label: "费用报销", color: "#FBBF24" },
  contract: { label: "合同审批", color: "#A78BFA" },
  leave: { label: "请假审批", color: "#60A5FA" },
  procurement: { label: "采购审批", color: "#34D399" },
  content_publish: { label: "内容发布", color: "#F472B6" },
  custom: { label: "自定义", color: "#9CA3AF" },
};

/** 审批模式展示配置 */
const MODE_CONFIG: Record<ApprovalMode, { label: string; color: string; desc: string }> = {
  countersign: { label: "会签", color: "#A78BFA", desc: "所有人审批通过即通过" },
  any_sign: { label: "或签", color: "#34D399", desc: "任一人审批即生效" },
  sequential: { label: "依次审批", color: "#60A5FA", desc: "按顺序逐人审批" },
};

/** 审批结果展示配置 */
const RESULT_CONFIG: Record<
  ApprovalResult,
  { label: string; color: string; bg: string; border: string }
> = {
  pending: { label: "待审批", color: "#FBBF24", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  approved: { label: "已通过", color: "#34D399", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  rejected: { label: "已拒绝", color: "#F87171", bg: "bg-red-500/10", border: "border-red-500/30" },
  timeout: { label: "已超时", color: "#FB923C", bg: "bg-orange-500/10", border: "border-orange-500/30" },
  transferred: { label: "已转交", color: "#60A5FA", bg: "bg-sky-500/10", border: "border-sky-500/30" },
};

/** 超时动作展示配置 */
const TIMEOUT_ACTION_CONFIG: Record<TimeoutAction, { label: string; color: string }> = {
  auto_approve: { label: "自动通过", color: "#34D399" },
  auto_reject: { label: "自动拒绝", color: "#F87171" },
  transfer: { label: "自动转交", color: "#60A5FA" },
};

/** Tab 类型 */
type ListTab = "pending_me" | "mine" | "all";

const TAB_CONFIG: Record<ListTab, { label: string; icon: typeof Inbox }> = {
  pending_me: { label: "待我审批", icon: Inbox },
  mine: { label: "我发起的", icon: Send },
  all: { label: "全部", icon: ListChecks },
};

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(prefix = "id"): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** ISO 时间转可读格式 MM-DD HH:MM */
function formatDateTime(iso?: string): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${mm}-${dd} ${hh}:${mi}`;
  } catch {
    return "—";
  }
}

/** 毫秒转可读时长 */
function formatDuration(ms?: number): string {
  if (ms === undefined || ms === null || isNaN(ms) || ms <= 0) return "—";
  const hours = ms / 3_600_000;
  if (hours >= 1) return `${hours.toFixed(1)}h`;
  const mins = ms / 60_000;
  if (mins >= 1) return `${mins.toFixed(0)}m`;
  return `${Math.round(ms / 1000)}s`;
}

/** 读取 localStorage 审批记录 */
function loadFlows(): ApprovalFlow[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ApprovalFlow[]) : [];
  } catch {
    return [];
  }
}

/** 写入 localStorage 审批记录 */
function saveFlows(flows: ApprovalFlow[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flows));
  } catch {
    // 容量超限或隐私模式，静默失败
  }
}

/** 获取类型展示配置 */
function getTypeConfig(type: ApprovalType) {
  return TYPE_CONFIG[type] || TYPE_CONFIG.custom;
}

/** 根据模板类型构建节点配置（非模板类型返回单个默认节点） */
function getTemplateNodes(type: ApprovalType): TemplateNodeConfig[] {
  if (type === "expense" || type === "contract" || type === "content_publish") {
    return APPROVAL_TEMPLATES[type].nodes.map((n) => ({ ...n }));
  }
  // 非模板类型：单个默认节点，用户可扩展
  return [{ name: "审批人", mode: "any_sign", timeoutHours: 24, timeoutAction: "auto_reject" }];
}

/** 根据模板类型获取默认标题 */
function getTemplateTitle(type: ApprovalType): string {
  if (type === "expense" || type === "contract" || type === "content_publish") {
    return APPROVAL_TEMPLATES[type].title;
  }
  return getTypeConfig(type).label;
}

// ============================================
// 审批核心逻辑
// ============================================

/**
 * 判定节点是否轮到某审批人（依次审批模式用）
 * 依次审批：当前轮到第一个尚未留下记录的审批人
 */
function isSequentialTurn(node: ApprovalNode, approver: string): boolean {
  const acted = new Set(node.records.map((r) => r.approver));
  for (const a of node.approvers) {
    if (!acted.has(a)) {
      return a === approver;
    }
  }
  return false;
}

/** 当前用户是否可对该节点执行审批 */
function canApproveNode(node: ApprovalNode, user: string): boolean {
  if (node.status !== "pending") return false;
  if (!node.approvers.includes(user)) return false;
  if (node.mode === "sequential") {
    return isSequentialTurn(node, user);
  }
  // 会签 / 或签：未留过记录的人可审批
  const acted = new Set(node.records.map((r) => r.approver));
  return !acted.has(user);
}

/** 获取依次审批模式下下一个该审批的人 */
function nextSequentialApprover(node: ApprovalNode): string | undefined {
  const acted = new Set(node.records.map((r) => r.approver));
  return node.approvers.find((a) => !acted.has(a));
}

/**
 * 处理一次审批动作，返回更新后的 flow（不可变更新）
 * - 通过/拒绝：根据模式判定节点是否终结
 * - 转交：将转交目标加入审批人列表，节点保持 pending
 */
function applyApproval(
  flow: ApprovalFlow,
  nodeIndex: number,
  approver: string,
  result: "approved" | "rejected" | "transferred",
  comment: string,
  attachments: string[],
  transferTarget?: string
): ApprovalFlow {
  const now = new Date().toISOString();
  const nodes = flow.nodes.map((n, i) =>
    i === nodeIndex ? { ...n, records: [...n.records] } : n
  );
  const node = { ...nodes[nodeIndex] };

  // 追加审批记录
  node.records.push({
    id: genId("rec"),
    approver,
    result,
    comment,
    attachments: attachments.length > 0 ? attachments : undefined,
    timestamp: now,
  });

  let flowStatus: ApprovalResult = flow.status;
  let currentNodeIndex = flow.currentNodeIndex;
  let completedAt: string | undefined = flow.completedAt;
  let totalDuration: number | undefined = flow.totalDuration;

  if (result === "transferred") {
    // 转交：将目标加入审批人（去重），节点保持 pending
    const target = (transferTarget || "").trim();
    if (target && !node.approvers.includes(target)) {
      node.approvers = [...node.approvers, target];
    }
    node.status = "pending";
  } else if (result === "rejected") {
    // 拒绝：节点立即拒绝，整条流程拒绝
    node.status = "rejected";
    flowStatus = "rejected";
    completedAt = now;
    totalDuration = Date.now() - new Date(flow.createdAt).getTime();
  } else {
    // 通过：根据模式判定节点是否完成
    if (node.mode === "any_sign") {
      // 或签：任一通过即节点通过
      node.status = "approved";
    } else if (node.mode === "countersign") {
      // 会签：所有审批人均通过才节点通过
      const approvedApprovers = new Set(
        node.records.filter((r) => r.result === "approved").map((r) => r.approver)
      );
      const allApproved = node.approvers.every((a) => approvedApprovers.has(a));
      node.status = allApproved ? "approved" : "pending";
    } else {
      // 依次审批：当前轮次通过，若还有下一个人则继续 pending
      const acted = new Set(node.records.map((r) => r.approver));
      const hasMore = node.approvers.some((a) => !acted.has(a));
      node.status = hasMore ? "pending" : "approved";
    }

    // 节点通过 → 推进到下一节点或完成流程
    if (node.status === "approved") {
      const nextIndex = nodeIndex + 1;
      if (nextIndex < nodes.length) {
        currentNodeIndex = nextIndex;
        nodes[nextIndex] = { ...nodes[nextIndex], activatedAt: now };
      } else {
        flowStatus = "approved";
        completedAt = now;
        totalDuration = Date.now() - new Date(flow.createdAt).getTime();
      }
    }
  }

  nodes[nodeIndex] = node;

  return {
    ...flow,
    nodes,
    currentNodeIndex,
    status: flowStatus,
    completedAt,
    totalDuration,
  };
}

/**
 * 超时处理：检查当前节点是否超时，按配置执行动作
 * 返回 { flow, changed } —— changed 标识是否有变更
 */
function checkTimeout(flow: ApprovalFlow): { flow: ApprovalFlow; changed: boolean } {
  if (flow.status !== "pending") return { flow, changed: false };
  const node = flow.nodes[flow.currentNodeIndex];
  if (!node || node.status !== "pending" || !node.activatedAt) {
    return { flow, changed: false };
  }
  const elapsed = Date.now() - new Date(node.activatedAt).getTime();
  const limit = node.timeoutHours * 3_600_000;
  if (elapsed < limit) return { flow, changed: false };

  // 已超时，按配置处理
  if (node.timeoutAction === "auto_approve") {
    const updated = applyApproval(
      flow,
      flow.currentNodeIndex,
      "系统(超时自动通过)",
      "approved",
      `节点超时（${node.timeoutHours}h），按配置自动通过`,
      []
    );
    return { flow: updated, changed: true };
  }
  if (node.timeoutAction === "auto_reject") {
    const updated = applyApproval(
      flow,
      flow.currentNodeIndex,
      "系统(超时自动拒绝)",
      "rejected",
      `节点超时（${node.timeoutHours}h），按配置自动拒绝`,
      []
    );
    return { flow: updated, changed: true };
  }
  // transfer：转交给 transferTo，重置激活时间重新计时
  if (node.timeoutAction === "transfer" && node.transferTo) {
    const updated = applyApproval(
      flow,
      flow.currentNodeIndex,
      "系统(超时转交)",
      "transferred",
      `节点超时（${node.timeoutHours}h），自动转交给 ${node.transferTo}`,
      [],
      node.transferTo
    );
    const nodes = updated.nodes.map((n, i) =>
      i === updated.currentNodeIndex ? { ...n, activatedAt: new Date().toISOString() } : n
    );
    return { flow: { ...updated, nodes }, changed: true };
  }
  return { flow, changed: false };
}

// ============================================
// 子组件：状态标签
// ============================================
function StatusBadge({ status }: { status: ApprovalResult }) {
  const cfg = RESULT_CONFIG[status];
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono ${cfg.bg} ${cfg.border} border`}
      style={{ color: cfg.color }}
    >
      {status === "pending" && <Clock size={9} />}
      {status === "approved" && <CheckCircle2 size={9} />}
      {status === "rejected" && <XCircle size={9} />}
      {status === "timeout" && <Timer size={9} />}
      {status === "transferred" && <UserCheck size={9} />}
      <span>{cfg.label}</span>
    </span>
  );
}

/** 模式标签 */
function ModeBadge({ mode }: { mode: ApprovalMode }) {
  const cfg = MODE_CONFIG[mode];
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono border"
      style={{ color: cfg.color, borderColor: `${cfg.color}40`, background: `${cfg.color}15` }}
      title={cfg.desc}
    >
      {cfg.label}
    </span>
  );
}

// ============================================
// 子组件：KPI 卡片
// ============================================
interface MiniKpiProps {
  label: string;
  value: string | number;
  icon: typeof Activity;
  color: string;
  delay?: number;
}

function MiniKpi({ label, value, icon: Icon, color, delay = 0 }: MiniKpiProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: "easeOut" }}
      className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-3 overflow-hidden"
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${color}80, transparent)` }}
      />
      <div
        className="absolute -top-8 -right-8 w-16 h-16 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`, filter: "blur(16px)" }}
      />
      <div className="flex items-center gap-2 mb-1.5 relative">
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center"
          style={{ background: `${color}15`, border: `1px solid ${color}30` }}
        >
          <Icon size={12} style={{ color }} />
        </div>
        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="text-[20px] font-mono font-bold text-white tracking-tight leading-none relative">
        {value}
      </div>
    </motion.div>
  );
}

// ============================================
// 子组件：空状态
// ============================================
function EmptyState({ text, sub }: { text: string; sub?: string }) {
  return (
    <div className="py-10 text-center">
      <div className="text-[12px] font-mono text-gray-600 italic">{text}</div>
      {sub && <div className="text-[10px] font-mono text-gray-700 mt-1.5">{sub}</div>}
    </div>
  );
}

// ============================================
// 子组件：审批流程可视化
// ============================================
function FlowVisualization({ flow, currentUser }: { flow: ApprovalFlow; currentUser: string }) {
  return (
    <div className="flex items-stretch gap-1 overflow-x-auto pb-2">
      {flow.nodes.map((node, idx) => {
        const isCurrent = idx === flow.currentNodeIndex && flow.status === "pending";
        const isDone = node.status === "approved";
        const isRejected = node.status === "rejected";
        const isTimeout = node.status === "timeout";
        const accent = isCurrent
          ? "#FBBF24"
          : isDone
          ? "#34D399"
          : isRejected
          ? "#F87171"
          : isTimeout
          ? "#FB923C"
          : "#4B5563";
        return (
          <div key={node.id} className="flex items-stretch shrink-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: idx * 0.05 }}
              className={`relative rounded-lg border bg-white/[0.02] backdrop-blur-md p-2.5 w-[180px] ${
                isCurrent ? "border-amber-500/40" : "border-white/[0.06]"
              }`}
            >
              {isCurrent && (
                <motion.div
                  className="absolute -inset-px rounded-lg pointer-events-none"
                  style={{ boxShadow: `0 0 0 1px ${accent}40` }}
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              )}
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-mono text-gray-500">#{idx + 1}</span>
                <StatusBadge status={node.status} />
              </div>
              <div className="text-[12px] font-medium text-white mb-1 truncate">{node.name}</div>
              <div className="flex items-center gap-1 mb-1.5">
                <ModeBadge mode={node.mode} />
              </div>
              <div className="flex items-center gap-1 text-[10px] font-mono text-gray-400 mb-1">
                <Users size={9} className="text-gray-500" />
                <span className="truncate" title={node.approvers.join("、")}>
                  {node.approvers.join("、") || "未配置"}
                </span>
              </div>
              <div className="flex items-center gap-1 text-[10px] font-mono text-gray-500">
                <Timer size={9} />
                <span>{node.timeoutHours}h</span>
                <span className="text-gray-700">·</span>
                <span style={{ color: TIMEOUT_ACTION_CONFIG[node.timeoutAction].color }}>
                  {TIMEOUT_ACTION_CONFIG[node.timeoutAction].label}
                </span>
              </div>
              {/* 当前节点：显示当前轮到谁审批 */}
              {isCurrent && (
                <div className="mt-1.5 pt-1.5 border-t border-white/[0.06]">
                  {node.mode === "sequential" ? (
                    <div className="text-[10px] font-mono text-amber-400">
                      待 {nextSequentialApprover(node) || "—"} 审批
                    </div>
                  ) : canApproveNode(node, currentUser) ? (
                    <div className="text-[10px] font-mono text-amber-400">待您审批</div>
                  ) : (
                    <div className="text-[10px] font-mono text-gray-500">等待审批人处理</div>
                  )}
                </div>
              )}
            </motion.div>
            {idx < flow.nodes.length - 1 && (
              <div className="flex items-center px-0.5">
                <ChevronRight
                  size={14}
                  className={isDone ? "text-emerald-500/60" : "text-gray-700"}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// 主组件
// ============================================
export default function ApprovalFlowEngine() {
  // 当前用户
  const currentUser = useUserStore((s) => s.currentUser);
  const me = currentUser?.displayName || currentUser?.username || "";

  // ===== 状态 =====
  const [flows, setFlows] = useState<ApprovalFlow[]>([]);
  const [activeTab, setActiveTab] = useState<ListTab>("pending_me");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // 审批操作表单状态
  const [actionComment, setActionComment] = useState<string>("");
  const [actionAttachments, setActionAttachments] = useState<string>("");
  const [transferTarget, setTransferTarget] = useState<string>("");
  const [actionMode, setActionMode] = useState<"approve" | "reject" | "transfer" | null>(null);

  // 已处理超时的 flow 集合（避免重复触发同一节点的同一次超时）
  const timeoutHandledRef = useRef<Set<string>>(new Set());

  // ===== 初始化加载 =====
  useEffect(() => {
    setFlows(loadFlows());
    setLoading(false);
  }, []);

  // ===== 超时定时检查 =====
  useEffect(() => {
    const timer = setInterval(() => {
      setFlows((prev) => {
        let changed = false;
        const next = prev.map((flow) => {
          // 用 flow.id + currentNodeIndex 作为超时处理去重键
          const dedupeKey = `${flow.id}_${flow.currentNodeIndex}`;
          const { flow: checked, changed: c } = checkTimeout(flow);
          if (c && !timeoutHandledRef.current.has(dedupeKey)) {
            timeoutHandledRef.current.add(dedupeKey);
            changed = true;
            return checked;
          }
          return flow;
        });
        if (changed) saveFlows(next);
        return changed ? next : prev;
      });
    }, TIMEOUT_CHECK_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  // ===== 持久化 =====
  const persistFlows = useCallback((next: ApprovalFlow[]) => {
    setFlows(next);
    saveFlows(next);
  }, []);

  // ===== 发起审批 =====
  const handleCreate = useCallback(
    (flow: ApprovalFlow) => {
      persistFlows([flow, ...flows]);
      setShowCreate(false);
      setSelectedId(flow.id);
    },
    [flows, persistFlows]
  );

  // ===== 执行审批动作 =====
  const handleApprove = useCallback(
    (flowId: string, result: "approved" | "rejected" | "transferred") => {
      const target = result === "transferred" ? transferTarget.trim() : undefined;
      if (result === "transferred" && !target) {
        return; // 转交必须指定目标
      }
      const attachments = actionAttachments
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const next = flows.map((flow) => {
        if (flow.id !== flowId) return flow;
        return applyApproval(
          flow,
          flow.currentNodeIndex,
          me,
          result,
          actionComment.trim(),
          attachments,
          target
        );
      });
      persistFlows(next);
      // 重置操作表单
      setActionComment("");
      setActionAttachments("");
      setTransferTarget("");
      setActionMode(null);
    },
    [flows, me, actionComment, actionAttachments, transferTarget, persistFlows]
  );

  // ===== 删除审批流（仅发起人可删） =====
  const handleDelete = useCallback(
    (flowId: string) => {
      persistFlows(flows.filter((f) => f.id !== flowId));
      if (selectedId === flowId) setSelectedId(null);
    },
    [flows, selectedId, persistFlows]
  );

  // ===== 统计 =====
  const stats = useMemo(() => {
    const pendingMe = flows.filter(
      (f) =>
        f.status === "pending" &&
        f.nodes[f.currentNodeIndex] &&
        canApproveNode(f.nodes[f.currentNodeIndex], me)
    ).length;
    const mine = flows.filter((f) => f.applicant === me).length;
    // 我已审批过的（任何节点留有我的记录）
    const approvedByMe = flows.filter((f) =>
      f.nodes.some((n) => n.records.some((r) => r.approver === me))
    ).length;
    const completed = flows.filter(
      (f) => f.status === "approved" || f.status === "rejected"
    );
    const approvedCount = completed.filter((f) => f.status === "approved").length;
    const passRate = completed.length > 0 ? (approvedCount / completed.length) * 100 : 0;
    const durations = completed
      .map((f) => f.totalDuration)
      .filter((d): d is number => typeof d === "number" && d > 0);
    const avgDuration =
      durations.length > 0 ? durations.reduce((s, d) => s + d, 0) / durations.length : 0;
    return { pendingMe, mine, approvedByMe, passRate, avgDuration };
  }, [flows, me]);

  // ===== 按类型 / 按人统计 =====
  const byTypeStats = useMemo(() => {
    const map = new Map<ApprovalType, { total: number; approved: number; rejected: number }>();
    for (const f of flows) {
      const entry = map.get(f.type) || { total: 0, approved: 0, rejected: 0 };
      entry.total += 1;
      if (f.status === "approved") entry.approved += 1;
      if (f.status === "rejected") entry.rejected += 1;
      map.set(f.type, entry);
    }
    return Array.from(map.entries()).map(([type, v]) => ({ type, ...v }));
  }, [flows]);

  const byPersonStats = useMemo(() => {
    const map = new Map<string, { approved: number; rejected: number; total: number }>();
    for (const f of flows) {
      for (const node of f.nodes) {
        for (const rec of node.records) {
          if (rec.result !== "approved" && rec.result !== "rejected") continue;
          const entry = map.get(rec.approver) || { approved: 0, rejected: 0, total: 0 };
          entry.total += 1;
          if (rec.result === "approved") entry.approved += 1;
          else entry.rejected += 1;
          map.set(rec.approver, entry);
        }
      }
    }
    return Array.from(map.entries())
      .map(([person, v]) => ({ person, ...v }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [flows]);

  // ===== Tab 过滤 + 排序 =====
  const filteredFlows = useMemo(() => {
    let list: ApprovalFlow[] = flows;
    if (activeTab === "pending_me") {
      list = flows.filter(
        (f) =>
          f.status === "pending" &&
          f.nodes[f.currentNodeIndex] &&
          canApproveNode(f.nodes[f.currentNodeIndex], me)
      );
    } else if (activeTab === "mine") {
      list = flows.filter((f) => f.applicant === me);
    }
    return [...list].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [flows, activeTab, me]);

  // ===== 选中详情 =====
  const selectedFlow = useMemo(
    () => flows.find((f) => f.id === selectedId) || null,
    [flows, selectedId]
  );

  // 当前用户能否对选中流程的当前节点审批
  const canActOnSelected = useMemo(() => {
    if (!selectedFlow || selectedFlow.status !== "pending") return false;
    const node = selectedFlow.nodes[selectedFlow.currentNodeIndex];
    if (!node) return false;
    return canApproveNode(node, me);
  }, [selectedFlow, me]);

  const hasData = flows.length > 0;

  // ===== 渲染 =====
  return (
    <div className="space-y-3">
      {/* ===== 顶部标题栏 ===== */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gavel size={16} className="text-violet-400" />
          <span className="text-[14px] font-medium text-white">审批流引擎</span>
          <span className="text-[10px] font-mono text-gray-600">
            {loading ? "加载中" : hasData ? `${flows.length} 条记录` : "本地模式"}
          </span>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1 px-2 py-1 rounded-md border border-violet-500/30 bg-violet-500/10 hover:bg-violet-500/20 transition-colors"
        >
          <Plus size={11} className="text-violet-400" />
          <span className="text-[11px] font-mono text-violet-300">发起审批</span>
        </button>
      </div>

      {/* ===== KPI 卡片区 ===== */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <MiniKpi label="待我审批" value={stats.pendingMe} icon={Inbox} color="#FBBF24" delay={0} />
        <MiniKpi label="我已审批" value={stats.approvedByMe} icon={CheckCircle2} color="#34D399" delay={0.05} />
        <MiniKpi label="我发起的" value={stats.mine} icon={Send} color="#60A5FA" delay={0.1} />
        <MiniKpi label="平均耗时" value={formatDuration(stats.avgDuration)} icon={Clock} color="#A78BFA" delay={0.15} />
        <MiniKpi
          label="通过率"
          value={`${stats.passRate.toFixed(0)}%`}
          icon={TrendingUp}
          color="#34D399"
          delay={0.2}
        />
      </div>

      {/* ===== 统计分析区（按类型 / 按人） ===== */}
      {hasData && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {/* 按类型统计 */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <FileText size={11} className="text-gray-400" />
              <span className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
                按类型统计
              </span>
            </div>
            <div className="space-y-1.5">
              {byTypeStats.length === 0 ? (
                <div className="text-[10px] font-mono text-gray-600 italic">暂无数据</div>
              ) : (
                byTypeStats.map((item) => {
                  const cfg = getTypeConfig(item.type);
                  const passRate = item.total > 0 ? (item.approved / item.total) * 100 : 0;
                  return (
                    <div key={item.type} className="flex items-center gap-2">
                      <span
                        className="text-[10px] font-mono w-16 shrink-0"
                        style={{ color: cfg.color }}
                      >
                        {cfg.label}
                      </span>
                      <div className="flex-1 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ background: cfg.color }}
                          initial={{ width: 0 }}
                          animate={{ width: `${passRate}%` }}
                          transition={{ duration: 0.6, ease: "easeOut" }}
                        />
                      </div>
                      <span className="text-[10px] font-mono text-gray-500 w-20 text-right shrink-0">
                        {item.approved}/{item.total} · {passRate.toFixed(0)}%
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* 按人统计 */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Users size={11} className="text-gray-400" />
              <span className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
                按人统计（审批数 Top8）
              </span>
            </div>
            <div className="space-y-1.5">
              {byPersonStats.length === 0 ? (
                <div className="text-[10px] font-mono text-gray-600 italic">暂无数据</div>
              ) : (
                byPersonStats.map((item) => {
                  const passRate = item.total > 0 ? (item.approved / item.total) * 100 : 0;
                  return (
                    <div key={item.person} className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-gray-300 w-20 truncate shrink-0" title={item.person}>
                        {item.person}
                      </span>
                      <div className="flex-1 h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                        <motion.div
                          className="h-full rounded-full bg-emerald-500/70"
                          initial={{ width: 0 }}
                          animate={{ width: `${passRate}%` }}
                          transition={{ duration: 0.6, ease: "easeOut" }}
                        />
                      </div>
                      <span className="text-[10px] font-mono text-gray-500 w-20 text-right shrink-0">
                        {item.approved}/{item.total} · {passRate.toFixed(0)}%
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== Tab 切换 ===== */}
      <div className="flex items-center gap-1 border-b border-white/[0.06]">
        {(Object.keys(TAB_CONFIG) as ListTab[]).map((tab) => {
          const cfg = TAB_CONFIG[tab];
          const active = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`relative flex items-center gap-1.5 px-3 py-1.5 transition-colors ${
                active ? "text-white" : "text-gray-500 hover:text-gray-300"
              }`}
            >
              <cfg.icon size={12} />
              <span className="text-[11px] font-mono">{cfg.label}</span>
              {active && (
                <motion.div
                  layoutId="approval-tab-underline"
                  className="absolute bottom-0 left-0 right-0 h-px bg-violet-400"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* ===== 审批列表 ===== */}
      <div className="space-y-1.5">
        {!hasData ? (
          <EmptyState text="暂无审批 / 点击发起" sub="点击右上角「发起审批」创建第一条审批流" />
        ) : filteredFlows.length === 0 ? (
          <EmptyState text="当前 Tab 暂无审批记录" />
        ) : (
          <AnimatePresence initial={false}>
            {filteredFlows.map((flow) => {
              const typeCfg = getTypeConfig(flow.type);
              const isSel = flow.id === selectedId;
              const currentNode = flow.nodes[flow.currentNodeIndex];
              return (
                <motion.div
                  key={flow.id}
                  layout
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.25 }}
                  onClick={() => setSelectedId(flow.id)}
                  className={`group cursor-pointer rounded-lg border backdrop-blur-md p-2.5 transition-colors ${
                    isSel
                      ? "border-violet-500/40 bg-violet-500/[0.04]"
                      : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-1 h-8 rounded-full shrink-0"
                      style={{ background: typeCfg.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[12px] font-medium text-white truncate">
                          {flow.title}
                        </span>
                        <StatusBadge status={flow.status} />
                      </div>
                      <div className="flex items-center gap-2 text-[10px] font-mono text-gray-500 flex-wrap">
                        <span style={{ color: typeCfg.color }}>{typeCfg.label}</span>
                        <span>·</span>
                        <span>申请人 {flow.applicant}</span>
                        {flow.amount !== undefined && flow.amount > 0 && (
                          <>
                            <span>·</span>
                            <span className="text-amber-400">¥{flow.amount.toLocaleString()}</span>
                          </>
                        )}
                        <span>·</span>
                        <span>{formatDateTime(flow.createdAt)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {/* 进度指示 */}
                      <div className="text-[10px] font-mono text-gray-500">
                        节点{" "}
                        {flow.status === "pending"
                          ? `${flow.currentNodeIndex + 1}/${flow.nodes.length}`
                          : `${flow.nodes.length}/${flow.nodes.length}`}
                      </div>
                      {/* 待我审批标记 */}
                      {flow.status === "pending" &&
                        currentNode &&
                        canApproveNode(currentNode, me) && (
                          <span className="px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-400 text-[9px] font-mono">
                            待我审批
                          </span>
                        )}
                      <ChevronRight
                        size={14}
                        className={`transition-transform ${isSel ? "rotate-90 text-violet-400" : "text-gray-600"}`}
                      />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      {/* ===== 选中审批详情 ===== */}
      <AnimatePresence>
        {selectedFlow && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-md p-3 space-y-3"
            >
              {/* 详情头部 */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[13px] font-medium text-white">{selectedFlow.title}</span>
                    <StatusBadge status={selectedFlow.status} />
                  </div>
                  <div className="text-[11px] font-mono text-gray-500">
                    {getTypeConfig(selectedFlow.type).label} · 申请人 {selectedFlow.applicant} ·{" "}
                    {formatDateTime(selectedFlow.createdAt)}
                    {selectedFlow.completedAt && (
                      <> · 完成 {formatDateTime(selectedFlow.completedAt)}</>
                    )}
                    {selectedFlow.totalDuration !== undefined && (
                      <> · 耗时 {formatDuration(selectedFlow.totalDuration)}</>
                    )}
                  </div>
                  {selectedFlow.content && (
                    <div className="mt-1.5 text-[11px] text-gray-400 leading-relaxed">
                      {selectedFlow.content}
                    </div>
                  )}
                </div>
                {selectedFlow.applicant === me && (
                  <button
                    onClick={() => handleDelete(selectedFlow.id)}
                    className="p-1 rounded text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0"
                    title="删除审批流"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>

              {/* 流程可视化 */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Activity size={11} className="text-violet-400" />
                  <span className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
                    审批流程
                  </span>
                </div>
                <FlowVisualization flow={selectedFlow} currentUser={me} />
              </div>

              {/* 节点状态 + 审批意见记录 */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <ListChecks size={11} className="text-violet-400" />
                  <span className="text-[11px] font-mono text-gray-400 uppercase tracking-wider">
                    节点审批记录
                  </span>
                </div>
                <div className="space-y-1.5">
                  {selectedFlow.nodes.map((node, idx) => (
                    <div
                      key={node.id}
                      className={`rounded-lg border p-2.5 ${
                        idx === selectedFlow.currentNodeIndex && selectedFlow.status === "pending"
                          ? "border-amber-500/30 bg-amber-500/[0.03]"
                          : "border-white/[0.06] bg-white/[0.01]"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-mono text-gray-600">#{idx + 1}</span>
                        <span className="text-[12px] font-medium text-white">{node.name}</span>
                        <ModeBadge mode={node.mode} />
                        <span className="ml-auto">
                          <StatusBadge status={node.status} />
                        </span>
                      </div>
                      {/* 审批人 / 超时配置 */}
                      <div className="flex items-center gap-1 text-[10px] font-mono text-gray-500 mb-1 flex-wrap">
                        <Users size={9} />
                        <span>审批人：{node.approvers.join("、") || "未配置"}</span>
                        <span className="text-gray-700">·</span>
                        <Timer size={9} />
                        <span>
                          超时 {node.timeoutHours}h → {TIMEOUT_ACTION_CONFIG[node.timeoutAction].label}
                        </span>
                        {node.transferTo && (
                          <>
                            <span className="text-gray-700">·</span>
                            <span>转交至 {node.transferTo}</span>
                          </>
                        )}
                      </div>
                      {/* 记录列表 */}
                      {node.records.length === 0 ? (
                        <div className="text-[10px] font-mono text-gray-700 italic pl-3">
                          暂无审批记录
                        </div>
                      ) : (
                        <div className="space-y-1 pl-3 border-l border-white/[0.06]">
                          {node.records.map((rec) => (
                            <div key={rec.id} className="flex items-start gap-2">
                              <span
                                className="text-[10px] font-mono shrink-0"
                                style={{ color: RESULT_CONFIG[rec.result].color }}
                              >
                                {RESULT_CONFIG[rec.result].label}
                              </span>
                              <span className="text-[10px] font-mono text-gray-400 shrink-0">
                                {rec.approver}
                              </span>
                              <span className="text-[10px] text-gray-400 flex-1 break-all">
                                {rec.comment || "（无意见）"}
                              </span>
                              {rec.attachments && rec.attachments.length > 0 && (
                                <span className="flex items-center gap-0.5 text-[9px] font-mono text-sky-400 shrink-0">
                                  <Paperclip size={9} />
                                  {rec.attachments.length}
                                </span>
                              )}
                              <span className="text-[9px] font-mono text-gray-600 shrink-0">
                                {formatDateTime(rec.timestamp)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* 审批操作区（仅当前用户可审批时显示） */}
              {canActOnSelected ? (
                <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.03] p-2.5 space-y-2">
                  <div className="flex items-center gap-1.5">
                    <UserCheck size={11} className="text-amber-400" />
                    <span className="text-[11px] font-mono text-amber-300 uppercase tracking-wider">
                      审批操作（当前节点：{selectedFlow.nodes[selectedFlow.currentNodeIndex].name}）
                    </span>
                  </div>
                  {/* 审批意见 */}
                  <textarea
                    value={actionComment}
                    onChange={(e) => setActionComment(e.target.value)}
                    placeholder="请输入审批意见…"
                    rows={2}
                    className="w-full rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[11px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40 resize-none"
                  />
                  {/* 附件 */}
                  <div className="flex items-center gap-1.5">
                    <Paperclip size={11} className="text-gray-500 shrink-0" />
                    <input
                      value={actionAttachments}
                      onChange={(e) => setActionAttachments(e.target.value)}
                      placeholder="附件名称，逗号分隔（如：合同.pdf, 发票.png）"
                      className="flex-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1 text-[11px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40"
                    />
                  </div>
                  {/* 转交目标 */}
                  <AnimatePresence>
                    {actionMode === "transfer" && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="flex items-center gap-1.5">
                          <User size={11} className="text-gray-500 shrink-0" />
                          <input
                            value={transferTarget}
                            onChange={(e) => setTransferTarget(e.target.value)}
                            placeholder="转交目标人姓名"
                            className="flex-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1 text-[11px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40"
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {/* 操作按钮 */}
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleApprove(selectedFlow.id, "approved")}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors"
                    >
                      <CheckCircle2 size={11} className="text-emerald-400" />
                      <span className="text-[11px] font-mono text-emerald-300">通过</span>
                    </button>
                    <button
                      onClick={() => handleApprove(selectedFlow.id, "rejected")}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 transition-colors"
                    >
                      <XCircle size={11} className="text-red-400" />
                      <span className="text-[11px] font-mono text-red-300">拒绝</span>
                    </button>
                    <button
                      onClick={() => {
                        if (actionMode === "transfer") {
                          handleApprove(selectedFlow.id, "transferred");
                        } else {
                          setActionMode("transfer");
                        }
                      }}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20 transition-colors"
                    >
                      <UserCheck size={11} className="text-sky-400" />
                      <span className="text-[11px] font-mono text-sky-300">
                        {actionMode === "transfer" ? "确认转交" : "转交"}
                      </span>
                    </button>
                    {actionMode === "transfer" && (
                      <button
                        onClick={() => {
                          setActionMode(null);
                          setTransferTarget("");
                        }}
                        className="px-2 py-1 rounded-md text-gray-500 hover:text-gray-300 transition-colors"
                      >
                        <X size={11} />
                      </button>
                    )}
                  </div>
                </div>
              ) : selectedFlow.status === "pending" ? (
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-2.5 text-[11px] font-mono text-gray-500">
                  <Clock size={11} className="inline mr-1 text-gray-600" />
                  当前节点等待审批人处理，您无需操作
                </div>
              ) : null}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== 发起审批弹窗 ===== */}
      <AnimatePresence>
        {showCreate && (
          <CreateApprovalModal
            me={me}
            onClose={() => setShowCreate(false)}
            onCreate={handleCreate}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 子组件：发起审批弹窗
// ============================================
interface CreateApprovalModalProps {
  me: string;
  onClose: () => void;
  onCreate: (flow: ApprovalFlow) => void;
}

/** 表单中的可编辑节点 */
interface FormNode {
  id: string;
  name: string;
  mode: ApprovalMode;
  approvers: string; // 逗号分隔
  timeoutHours: number;
  timeoutAction: TimeoutAction;
  transferTo: string;
}

function CreateApprovalModal({ me, onClose, onCreate }: CreateApprovalModalProps) {
  const [type, setType] = useState<ApprovalType>("expense");
  const [title, setTitle] = useState<string>(APPROVAL_TEMPLATES.expense.title);
  const [content, setContent] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [nodes, setNodes] = useState<FormNode[]>(() =>
    getTemplateNodes("expense").map((n) => ({ ...n, id: genId("fn"), approvers: "", transferTo: "" }))
  );

  // 类型切换：重新加载模板节点和标题
  const handleTypeChange = useCallback((newType: ApprovalType) => {
    setType(newType);
    setTitle(getTemplateTitle(newType));
    setNodes(
      getTemplateNodes(newType).map((n) => ({
        ...n,
        id: genId("fn"),
        approvers: "",
        transferTo: "",
      }))
    );
  }, []);

  // 节点增删改
  const updateNode = useCallback((id: string, patch: Partial<FormNode>) => {
    setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch } : n)));
  }, []);

  const addNode = useCallback(() => {
    setNodes((prev) => [
      ...prev,
      {
        id: genId("fn"),
        name: `审批节点 ${prev.length + 1}`,
        mode: "any_sign",
        approvers: "",
        timeoutHours: 24,
        timeoutAction: "auto_reject",
        transferTo: "",
      },
    ]);
  }, []);

  const removeNode = useCallback((id: string) => {
    setNodes((prev) => (prev.length > 1 ? prev.filter((n) => n.id !== id) : prev));
  }, []);

  // 提交
  const handleSubmit = useCallback(() => {
    if (!me) return;
    if (!title.trim()) return;
    // 至少一个节点有审批人
    const validNodes = nodes.filter((n) => n.approvers.trim());
    if (validNodes.length === 0) return;

    const now = new Date().toISOString();
    const builtNodes: ApprovalNode[] = validNodes.map((n, idx) => ({
      id: genId("node"),
      name: n.name.trim() || `审批节点 ${idx + 1}`,
      approvers: n.approvers
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      mode: n.mode,
      order: idx,
      timeoutHours: n.timeoutHours,
      timeoutAction: n.timeoutAction,
      transferTo: n.transferTo.trim() || undefined,
      status: "pending",
      records: [],
      activatedAt: idx === 0 ? now : undefined,
    }));

    const flow: ApprovalFlow = {
      id: genId("flow"),
      title: title.trim(),
      type,
      content: content.trim(),
      amount: amount.trim() ? Number(amount) : undefined,
      applicant: me,
      nodes: builtNodes,
      currentNodeIndex: 0,
      status: "pending",
      createdAt: now,
    };
    onCreate(flow);
  }, [me, title, content, amount, type, nodes, onCreate]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl border border-white/[0.06] bg-[#0a0a0f]/95 backdrop-blur-xl p-4 space-y-3"
      >
        {/* 顶部光带 */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/60 to-transparent" />

        {/* 标题栏 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gavel size={14} className="text-violet-400" />
            <span className="text-[13px] font-medium text-white">发起审批</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded text-gray-500 hover:text-gray-300 hover:bg-white/[0.04] transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* 审批类型 */}
        <div>
          <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            审批类型
          </label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {(Object.keys(TYPE_CONFIG) as ApprovalType[]).map((t) => {
              const cfg = TYPE_CONFIG[t];
              const active = type === t;
              return (
                <button
                  key={t}
                  onClick={() => handleTypeChange(t)}
                  className={`px-2 py-1 rounded-md text-[11px] font-mono border transition-colors ${
                    active
                      ? "text-white"
                      : "text-gray-400 border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
                  }`}
                  style={
                    active
                      ? { color: cfg.color, borderColor: `${cfg.color}40`, background: `${cfg.color}15` }
                      : undefined
                  }
                >
                  {cfg.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 标题 */}
        <div>
          <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            审批标题
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full mt-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[12px] text-gray-200 font-mono outline-none focus:border-violet-500/40"
          />
        </div>

        {/* 内容 */}
        <div>
          <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            审批内容
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={2}
            placeholder="请描述审批事项…"
            className="w-full mt-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[11px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40 resize-none"
          />
        </div>

        {/* 金额（可选） */}
        <div>
          <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
            金额（可选，报销/采购类）
          </label>
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder="数字"
            className="w-full mt-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1.5 text-[12px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40"
          />
        </div>

        {/* 审批节点配置 */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="flex items-center gap-1 text-[10px] font-mono text-gray-500 uppercase tracking-wider">
              <Settings2 size={11} />
              审批节点配置
            </label>
            <button
              onClick={addNode}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono text-violet-300 border border-violet-500/30 bg-violet-500/10 hover:bg-violet-500/20 transition-colors"
            >
              <Plus size={10} />
              添加节点
            </button>
          </div>
          <div className="space-y-1.5">
            {nodes.map((n, idx) => (
              <div
                key={n.id}
                className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-2 space-y-1.5"
              >
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-mono text-gray-600 shrink-0">#{idx + 1}</span>
                  <input
                    value={n.name}
                    onChange={(e) => updateNode(n.id, { name: e.target.value })}
                    placeholder="节点名称（如 部门经理）"
                    className="flex-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1 text-[11px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40"
                  />
                  <select
                    value={n.mode}
                    onChange={(e) => updateNode(n.id, { mode: e.target.value as ApprovalMode })}
                    className="rounded-md border border-white/[0.06] bg-white/[0.02] px-1.5 py-1 text-[10px] text-gray-300 font-mono outline-none focus:border-violet-500/40"
                  >
                    <option value="any_sign">或签</option>
                    <option value="countersign">会签</option>
                    <option value="sequential">依次审批</option>
                  </select>
                  {nodes.length > 1 && (
                    <button
                      onClick={() => removeNode(n.id)}
                      className="p-1 rounded text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors shrink-0"
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <Users size={11} className="text-gray-500 shrink-0" />
                  <input
                    value={n.approvers}
                    onChange={(e) => updateNode(n.id, { approvers: e.target.value })}
                    placeholder="审批人，逗号分隔（如 张三, 李四）"
                    className="flex-1 rounded-md border border-white/[0.06] bg-white/[0.02] px-2 py-1 text-[11px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40"
                  />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Timer size={11} className="text-gray-500 shrink-0" />
                  <span className="text-[10px] font-mono text-gray-500">超时</span>
                  <input
                    type="number"
                    value={n.timeoutHours}
                    min={1}
                    onChange={(e) =>
                      updateNode(n.id, { timeoutHours: Math.max(1, Number(e.target.value) || 1) })
                    }
                    className="w-16 rounded-md border border-white/[0.06] bg-white/[0.02] px-1.5 py-1 text-[11px] text-gray-200 font-mono outline-none focus:border-violet-500/40"
                  />
                  <span className="text-[10px] font-mono text-gray-500">小时</span>
                  <span className="text-[10px] font-mono text-gray-600">·</span>
                  <select
                    value={n.timeoutAction}
                    onChange={(e) =>
                      updateNode(n.id, { timeoutAction: e.target.value as TimeoutAction })
                    }
                    className="rounded-md border border-white/[0.06] bg-white/[0.02] px-1.5 py-1 text-[10px] text-gray-300 font-mono outline-none focus:border-violet-500/40"
                  >
                    <option value="auto_approve">自动通过</option>
                    <option value="auto_reject">自动拒绝</option>
                    <option value="transfer">自动转交</option>
                  </select>
                  {n.timeoutAction === "transfer" && (
                    <input
                      value={n.transferTo}
                      onChange={(e) => updateNode(n.id, { transferTo: e.target.value })}
                      placeholder="转交目标人"
                      className="w-28 rounded-md border border-white/[0.06] bg-white/[0.02] px-1.5 py-1 text-[11px] text-gray-200 placeholder-gray-600 font-mono outline-none focus:border-violet-500/40"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 提交按钮 */}
        <div className="flex items-center justify-end gap-1.5 pt-1">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-[11px] font-mono text-gray-400 border border-white/[0.06] hover:bg-white/[0.04] transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || !nodes.some((n) => n.approvers.trim())}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-mono text-white border border-violet-500/40 bg-violet-500/20 hover:bg-violet-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send size={11} />
            发起
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
